import {
  GetAllCompanyDto,
  UpdateCompanyParametersDto,
  GetCompanyParametersDto,
  GetCompanyReducedResponseDto,
  UpdateCompanyDto,
  GetCompanyQueryDto,
  GetAllCompaniesQueryDto,
} from "../types/api/api-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { axiosApi } from "./axios";

//#region V1
// Non paginated company list for bo
const getCompanyBoListV1 = async (payload: GetAllCompaniesQueryDto) => {
  const response = await axiosApi.get<GetCompanyReducedResponseDto>(`v1/company/bo/list`, {
    params: payload,
  });
  return response.data;
};

export const useGetCompanyBoListV1 = (payload?: GetAllCompaniesQueryDto) => {
  return useQuery({
    queryKey: ["companyBoList", payload],
    queryFn: () => getCompanyBoListV1(payload),
  });
};

const getCompanyBOV1 = async (id: string, query?: GetCompanyQueryDto) => {
  const response = await axiosApi.get<GetAllCompanyDto>(`/v1/company/bo/${id}`, {
    params: query,
  });
  return response.data;
};

export const useGetCompanyBOV1 = (id: string, query?: GetCompanyQueryDto) => {
  return useQuery({
    queryKey: ["getCompanyBOV1", id],
    queryFn: () => getCompanyBOV1(id, query),
    enabled: !!id,
  });
};

const updateCompanyBoV1 = async ({ id, payload }: { id: string; payload: UpdateCompanyDto }) => {
  const response = await axiosApi.patch<GetAllCompanyDto>(`/v1/company/bo/${id}`, payload);
  return response.data;
};

export const useUpdateCompanyBoV1 = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");

  return useMutation({
    mutationFn: updateCompanyBoV1,
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: ["getCompanyBOV1", id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tarif"],
      });
      toast.success(t("companyApi.updateSuccess"));
    },
    onError: () => {
      toast.error(t("companyApi.updateError"));
    },
  });
};

//#endregion

const getCompany = async () => {
  const response = await axiosApi.get<GetAllCompanyDto>(`v1/company`);
  return response.data;
};

export const useGetCompany = () => {
  return useQuery({
    queryKey: ["company"],
    queryFn: () => getCompany(),
    staleTime: 1000 * 60 * 1, // 1 minute
    gcTime: 1000 * 60 * 1, // 1 minute
  });
};

const updateCompanyParametersBO = async ({
  id,
  payload,
}: {
  id: string;
  payload: UpdateCompanyParametersDto;
}) => {
  return axiosApi.patch(`/company/backOffice/updateCompanyParameters/${id}`, payload);
};

export const useUpdateCompanyParametersBO = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");

  return useMutation({
    mutationFn: updateCompanyParametersBO,
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: ["getCompanyParameters", data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["getFullCompanyBO", data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["getCompanyBOV1", data.id],
      });
    },
    onError: () => {
      toast.error(t("companyApi.updateError"));
    },
  });
};

const getCompanyParametersBo = async (id: string) => {
  const response = await axiosApi.get<GetCompanyParametersDto>(
    `/company/backOffice/companyParameters/${id}`,
  );
  return response.data;
};

export const useGetCompanyParametersBo = (id: string) => {
  return useQuery({
    queryKey: ["getCompanyParameters", id],
    queryFn: () => getCompanyParametersBo(id),
  });
};
